import React, { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { openStorageFile } from "@/utils/storageRefs";
import { useToast } from "@/components/ui/use-toast";
import {
  Upload,
  File,
  Download,
  Trash2,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Calendar,
  Loader2,
  Plus,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DocumentAttachment } from "@/models";

interface CompactFileUploaderProps {
  documentId?: number;
  attachments: DocumentAttachment[];
  uploading: boolean;
  onFileUpload: (file: File) => void;
  onDeleteAttachment: (attachment: DocumentAttachment) => void;
  onStatusChange: (
    attachment: DocumentAttachment,
    newStatus: "upcoming" | "current" | "expired" | "disabled"
  ) => void;
  onOpenExpirationDialog: (attachment: DocumentAttachment) => void;
  onOpenEffectiveDateDialog: (attachment: DocumentAttachment) => void;
  acceptedFileTypes?: string;
  maxFileSize?: string;
  disabled?: boolean;
}

const CompactFileUploader: React.FC<CompactFileUploaderProps> = ({
  documentId,
  attachments,
  uploading,
  onFileUpload,
  onDeleteAttachment,
  onStatusChange,
  onOpenExpirationDialog,
  onOpenEffectiveDateDialog,
  acceptedFileTypes = ".pdf,.docx,.doc,.txt,.csv",
  maxFileSize = "10MB",
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "upcoming":
        return <Calendar className="h-3 w-3 text-blue-600" />;
      case "current":
        return <CheckCircle className="h-3 w-3 text-green-600" />;
      case "disabled":
        return <XCircle className="h-3 w-3 text-gray-600" />;
      case "expired":
        return <AlertTriangle className="h-3 w-3 text-red-600" />;
      default:
        return <CheckCircle className="h-3 w-3 text-green-600" />;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "upcoming":
        return "Upcoming";
      case "current":
        return "Current";
      case "disabled":
        return "Disabled";
      case "expired":
        return "Expired";
      default:
        return "Current";
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "upcoming":
        return "bg-blue-50 text-blue-700 border-blue-200";
      case "current":
        return "bg-green-50 text-green-700 border-green-200";
      case "disabled":
        return "bg-gray-50 text-gray-700 border-gray-200";
      case "expired":
        return "bg-red-50 text-red-700 border-red-200";
      default:
        return "bg-green-50 text-green-700 border-green-200";
    }
  };

  const isExpiringSoon = (expires_at?: string) => {
    if (!expires_at) return false;
    const expirationDate = new Date(expires_at);
    const now = new Date();
    const threeDaysFromNow = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
    return expirationDate <= threeDaysFromNow && expirationDate > now;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFileUpload(e.target.files[0]);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (disabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Group current and upcoming files together, separate disabled/expired
  const activeFiles = attachments.filter(
    (att) => att.status === "current" || att.status === "upcoming"
  );
  const inactiveFiles = attachments.filter(
    (att) => att.status === "disabled" || att.status === "expired"
  );

  return (
    <div className="space-y-3">
      {/* Upload indicator when file is being uploaded */}
      {uploading && (
        <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
          <div className="flex-1">
            <p className="text-sm font-medium text-blue-900">Uploading file...</p>
            <p className="text-xs text-blue-700">Please wait while your file is being uploaded.</p>
          </div>
        </div>
      )}

      {/* Compact Upload Area */}
      <div
        className={`flex items-center gap-3 p-3 border-2 border-dashed rounded-lg transition-all ${
          disabled 
            ? "border-gray-200 bg-gray-50" 
            : uploading 
              ? "border-blue-300 bg-blue-50" 
              : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <Upload className={`h-5 w-5 ${uploading ? "text-blue-500" : "text-gray-400"}`} />
        <div className="flex-1">
          <p className={`text-sm font-medium ${uploading ? "text-blue-700" : "text-gray-700"}`}>
            {uploading ? "Uploading..." : "Drop files here or"}{" "}
            {!uploading && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled || uploading}
                className="text-blue-600 hover:text-blue-700 underline"
              >
                browse
              </button>
            )}
          </p>
          <p className="text-xs text-gray-500">
            {acceptedFileTypes.replace(/\./g, "").toUpperCase()} (Max {maxFileSize})
          </p>
        </div>
        
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept={acceptedFileTypes}
          style={{ display: "none" }}
          disabled={disabled || uploading}
        />
        
        {uploading && (
          <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
        )}
      </div>

      {disabled && (
        <p className="text-xs text-orange-600 bg-orange-50 px-2 py-1 rounded text-center">
          Save document first to enable file uploads
        </p>
      )}

      {/* Active Files - Compact List */}
      {activeFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <File className="h-4 w-4" />
            Files ({activeFiles.length})
          </h4>
          <div className="space-y-1">
            {activeFiles.map((attachment) => (
              <div
                key={attachment.id}
                className={`flex items-center gap-2 p-2 rounded-md border ${getStatusColor(
                  attachment.status
                )}`}
              >
                <div className="flex items-center gap-2">
                  {getStatusIcon(attachment.status)}
                  <span className="text-xs font-medium text-current">
                    {getStatusLabel(attachment.status)}
                  </span>
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-medium truncate">
                      {attachment.file_name || "Unknown file"}
                    </p>
                    {isExpiringSoon(attachment.expires_at) && (
                      <Badge variant="outline" className="text-xs px-1 py-0 h-4">
                        <Clock className="h-2 w-2 mr-1" />
                        Soon
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>
                      {attachment.file_size &&
                        `${Math.round(attachment.file_size / 1024)} KB`}
                    </span>
                    {attachment.expires_at && attachment.status !== "upcoming" && (
                      <span>
                        Expires: {new Date(attachment.expires_at).toLocaleDateString()}
                      </span>
                    )}
                    {attachment.status === "upcoming" && attachment.effective_date && (
                      <span className="text-blue-600">
                        Active: {new Date(attachment.effective_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => void openStorageFile(attachment.file_path, { defaultBucket: "document_files" })}
                    title="Download"
                    disabled={
                      attachment.status === "expired" ||
                      attachment.status === "disabled"
                    }
                  >
                    <Download className="h-3 w-3" />
                  </Button>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        title="Manage"
                      >
                        <Clock className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => onStatusChange(attachment, "upcoming")}
                        disabled={attachment.status === "upcoming"}
                      >
                        <Calendar className="h-3 w-3 mr-2 text-blue-600" />
                        Set Upcoming
                      </DropdownMenuItem>
                      {attachment.status === "upcoming" && (
                        <DropdownMenuItem
                          onClick={() => onOpenEffectiveDateDialog(attachment)}
                        >
                          <Calendar className="h-3 w-3 mr-2 text-blue-600" />
                          Set Effective Date
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => onStatusChange(attachment, "current")}
                        disabled={attachment.status === "current"}
                      >
                        <CheckCircle className="h-3 w-3 mr-2 text-green-600" />
                        Set Current
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onStatusChange(attachment, "disabled")}
                        disabled={attachment.status === "disabled"}
                      >
                        <XCircle className="h-3 w-3 mr-2 text-gray-600" />
                        Set Disabled
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onOpenExpirationDialog(attachment)}
                      >
                        <Clock className="h-3 w-3 mr-2" />
                        Set Expiration
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                    onClick={() => onDeleteAttachment(attachment)}
                    title="Delete"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inactive Files - Collapsed by default */}
      {inactiveFiles.length > 0 && (
        <details className="group">
          <summary className="cursor-pointer text-sm font-medium text-gray-600 hover:text-gray-800 flex items-center gap-2">
            <span className="group-open:rotate-90 transition-transform">▶</span>
            Inactive Files ({inactiveFiles.length})
          </summary>
          <div className="mt-2 space-y-1 pl-4">
            {inactiveFiles.map((attachment) => (
              <div
                key={attachment.id}
                className={`flex items-center gap-2 p-2 rounded-md border ${getStatusColor(
                  attachment.status
                )}`}
              >
                <div className="flex items-center gap-2">
                  {getStatusIcon(attachment.status)}
                  <span className="text-xs font-medium text-current">
                    {getStatusLabel(attachment.status)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate">
                    {attachment.file_name || "Unknown file"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {attachment.file_size &&
                      `${Math.round(attachment.file_size / 1024)} KB`}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        title="Manage"
                      >
                        <Clock className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => onStatusChange(attachment, "current")}
                      >
                        <CheckCircle className="h-3 w-3 mr-2 text-green-600" />
                        Restore as Current
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onStatusChange(attachment, "upcoming")}
                      >
                        <Calendar className="h-3 w-3 mr-2 text-blue-600" />
                        Set as Upcoming
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                    onClick={() => onDeleteAttachment(attachment)}
                    title="Delete"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Empty state */}
      {attachments.length === 0 && !uploading && (
        <div className="text-center py-6 text-gray-500">
          <File className="mx-auto h-8 w-8 mb-2 opacity-50" />
          <p className="text-sm">No files uploaded yet</p>
        </div>
      )}
    </div>
  );
};

export default CompactFileUploader; 